import * as React from 'react'
import { Spinner } from '$views/components/Spinner'
import { LoadGoogleMapsApi } from '$utils/loadGoogleMapsApi'
import { formatError } from '$views/components/Error/Error.mapper'
import { ErrorView } from '$views/components/Error/Error.model'
import { Error } from '$views/components/Error'
import createHTMLMapMarker from '$utils/htmlMapMarker'
import './FullMapCaretakers.css'
import { getCaretakersLocation } from '$services/caretaker/caretakersLocation/caretakersLocation.service'
import MarkerClusterer, { MarkerClustererOptions } from '@googlemaps/markerclustererplus'
import { FontFamily, theme } from '$utils/theme'
import {
  CaretakerLocation,
  caretakersLocationOffset,
  caretakersLocationLimit,
  CaretakerLocationFilters,
} from '$domain/model/caretaker/caretakersLocation/caretakersLocation.model'
import { draft } from '$utils/services/formDraftService'
import { Text, Block } from '$views/components'
import { Icon } from '$views/components/Icon'
import eye from '$icons/eye.svg'
import { TabButton } from '$views/components/TabButton'
import history from '$utils/history'
import { routeProvider } from '$utils/routeProvider'
import {
  FullMapCaretakersWrapper,
  StyledAskNearCaretakersButton,
  FullMapWrapper,
  StyledSearchBlock,
  ContainerSearchBlock,
  SearchFamilyWrapper,
  ContainerSearchFamily,
} from '$views/FullMapCaretakers/FullMapCaretakers.style'
import { LocationSearchInput } from '$views/FullMapCaretakers/components/LocationSearchInput'
import { SearchHereButton } from '$views/FullMapCaretakers/components/SearchHereButton'
import { CaretakerCard } from '$views/FullMapCaretakers/components/CaretakerCard'
import { MediaQueryOwn } from '$views/components/MediaQueryOwn'
import { SearchInputModal } from '$views/FullMapCaretakers/components/SearchInputModal/SearchInputModal'
import { urlParamsParser } from '$infrastructure/utils/urlParamsParser'
import { PrivateHeader } from '$views/components/Layout/PrivateHeader'
import { isUserAuthenticated } from '$services/session/isAuthenticated/isAuthenticated.service'
import { PublicHeader } from '$views/components/Layout/PublicHeader'
import { broadcastFormDraftName, broadcastFormSteps } from '$views/RecommendationsForms/BroadcastForm/BroadcastForm'
import { openInNewTab } from '$utils/openNewTab'
import { isDesktop } from '$utils/theme/media'

const proIcon = require('$icons/pro.svg')
const verifiedIcon = require('$icons/verifiedCheck.svg')
const unverifiedIcon = require('$icons/unverifiedCheck.svg')

export const fullMapDraft = 'fullMapDraft'

const mapOptions = {
  mapId: 'e0fdf2e832b55167',
  mapTypeControl: false,
  gestureHandling: 'greedy',
  fullscreenControl: false,
  overviewMapControl: false,
  panControl: true,
  streetViewControl: false,
  zoomControl: false,
  clickableIcons: false,
  minZoom: 2,
  maxZoom: 20,
  restriction: {
    latLngBounds: {
      east: 179.9999,
      north: 85,
      south: -85,
      west: -179.9999,
    },
    strictBounds: true,
  },
}

const calculator = (markers, clusterIconStylesCount) => {
  return {
    text: `${markers.length}+`,
    index: 1000001,
    title: undefined,
  }
}

const mcOptions: MarkerClustererOptions = {
  styles: [
    {
      width: 36,
      height: 36,
      fontFamily: FontFamily.base,
    },
  ],
  calculator,
  minimumClusterSize: 3,
}

interface WindowWithNativeProps extends Window {
  ReactNativeWebView?: any
}

export const FullMapCaretakers: React.FC = () => {
  const windowsWithNativeProps: WindowWithNativeProps = window
  let map: React.RefObject<HTMLDivElement> = React.useRef(undefined)
  const [stateMap, setStateMap] = React.useState<google.maps.Map>(undefined)
  const [stateMarkers, setStateMarkers] = React.useState<any[]>([])
  const [stateCluster, setStateCluster] = React.useState<MarkerClusterer>(undefined)
  const [address, setAddress] = React.useState<string>('')
  const [errorNotification, setErrorNotification] = React.useState<ErrorView>(undefined)
  const [showErrorNotification, setShowErrorNotification] = React.useState<boolean>(false)
  const [isLoading, handleLoading] = React.useState<boolean>(true)
  const [isLoadingSearchHereButton, handleLoadingSearchHereButton] = React.useState<boolean>(false)
  const [activeCaretakers, setActiveCaretakers] = React.useState<CaretakerLocation[]>([])
  const [familySearching, setFamilySearching] = React.useState<Number>(0)
  const [filters, setFilters] = React.useState<CaretakerLocationFilters[]>([])
  const [showRefreshButton, handleRefreshButton] = React.useState<boolean>(false)
  const [showBottomButton, handleBottomButton] = React.useState<boolean>(true)
  const [showSearchInputModal, handleSearchInputModal] = React.useState<boolean>(true)
  const [innerHeight, setInnerHeight] = React.useState<number>(0)

  const getUrlParams = () => {
    const urlParam = urlParamsParser(location.search) as {
      filter: string
    }

    return {
      filter: urlParam.filter,
    }
  }

  const saveFullMapDraft = newValues => {
    const draftValues = draft.get(fullMapDraft)
    draft.set(fullMapDraft, {
      ...draftValues,
      ...newValues,
    })
  }

  const saveDraftLocation = (mapOfGoogleMaps, actualAddress) => {
    saveFullMapDraft({
      location: {
        position: {
          lat: mapOfGoogleMaps.getCenter().lat(),
          lng: mapOfGoogleMaps.getCenter().lng(),
          zoom: mapOfGoogleMaps.getZoom(),
          address: actualAddress ? actualAddress : draft.get(fullMapDraft).location.position.address,
        },
      },
    })
  }

  const setFamilySearchingNumber = () => setFamilySearching(Math.floor(Math.random() * (17 - 5) + 5))

  const setErrors = errors => {
    setErrorNotification(errors)
    setShowErrorNotification(true)
  }

  const onClickErrorNotification = () => setShowErrorNotification(false)

  const shortenName = name => {
    if (name.length >= 10) {
      return `${name.slice(0, 8).trim()}...`
    }
    return name
  }

  const isVerified = caretaker => {
    if ((process.env.REACT_APP_AUXILIARES as string) !== 'true' || caretaker.isVerified) {
      return `<span class="verified-icon">${verifiedIcon}</span>`
    }

    return `<span class="verified-icon">${unverifiedIcon}</span>`
  }

  const isPro = caretaker => {
    if (caretaker.hasActiveProSubscription) {
      return `<span class="pro-icon">${proIcon}</span>`
    }

    return ``
  }

  const getUnselectedHtlmMarker = caretaker => {
    return `<div class="pin"><div class="pin-wrapper ${caretaker.hasActiveProSubscription &&
      'pro'}"><div class="pin-container"><span class="pin-text">${shortenName(caretaker.name)}</span>${isVerified(
      caretaker,
    )}</div>${isPro(caretaker)}</div></div>`
  }

  const getSelectedHtlmMarker = caretaker => {
    return `<div class="pin"><div class="pin-wrapper selected ${caretaker.hasActiveProSubscription &&
      'pro'}"><div class="pin-container"><span class="pin-text">${shortenName(caretaker.name)}</span>${isVerified(
      caretaker,
    )}</div>${isPro(caretaker)}</div></div>`
  }

  const getDefaultLocation = async () => {
    const draftLocation = draft.get(fullMapDraft)
    if (draftLocation.location && draftLocation.location.position) {
      handleSearchInputModal(false)
      setAddress(draftLocation.location.position.address)
      return {
        lat: draftLocation.location.position.lat,
        lng: draftLocation.location.position.lng,
      }
    }
    return {
      lat: parseFloat(process.env.REACT_APP_GOOGLE_MAPS_DEFAULT_LAT as string),
      lng: parseFloat(process.env.REACT_APP_GOOGLE_MAPS_DEFAULT_LNG as string),
    }
  }

  const getDefaultZoom = () => {
    const draftLocation = draft.get(fullMapDraft)
    if (draftLocation.location && draftLocation.location.position && draftLocation.location.position.zoom) {
      return draftLocation.location.position.zoom
    }

    return parseInt(process.env.REACT_APP_GOOGLE_MAPS_ZOOM as string, 10)
  }

  const loadGoogleMaps = async (defaultLocation, defaultZoom) => {
    try {
      const googleMapsApi = await LoadGoogleMapsApi.get({
        key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_CLIENT,
        v: '3.exp',
        libraries: ['geometry', 'drawing', 'places'],
      })
      let googleMapsMap: google.maps.Map
      if (map.current) {
        googleMapsMap = new googleMapsApi.Map(map.current, {
          center: defaultLocation,
          zoom: defaultZoom,
          ...mapOptions,
        })
      }
      setStateMap(googleMapsMap)
      handleLoading(false)
      return googleMapsMap
    } catch (error) {
      const googleMapError = formatError(error)
      setErrorNotification(googleMapError)
      setShowErrorNotification(true)
      handleLoading(false)
      return undefined
    }
  }

  const setBounds = place => {
    const mapBounds = new google.maps.LatLngBounds()

    if (place.geometry.viewport) {
      mapBounds.union(place.geometry.viewport)
    } else {
      mapBounds.extend(place.geometry.location)
    }
    stateMap.fitBounds(mapBounds)
  }

  const getHtmlMarker = (caretaker, caretakerSelected) => {
    if (caretaker && caretakerSelected) {
      return caretakerSelected.caretakerId === caretaker.caretakerId
        ? getSelectedHtlmMarker(caretaker)
        : getUnselectedHtlmMarker(caretaker)
    }

    return getUnselectedHtlmMarker(caretaker)
  }

  const createMarkers = (caretakers, googleMapsMap) => {
    let markers = []

    caretakers.map(caretaker => {
      const latLng = new google.maps.LatLng(caretaker.latitude, caretaker.longitude)
      const caretakerSelected = draft.get(fullMapDraft).caretaker
      const marker = createHTMLMapMarker({
        latlng: latLng,
        html: getHtmlMarker(caretaker, caretakerSelected),
        caretaker: caretaker,
      })
      if (caretaker && caretakerSelected && caretakerSelected.caretakerId === caretaker.caretakerId) {
        setActiveCaretakers([caretakerSelected])
        handleBottomButton(false)
      }

      const bounds = googleMapsMap.getBounds()
      if (bounds && bounds.contains(latLng) === true) {
        marker.setMap(googleMapsMap)
      }

      markers.push(marker)
    })

    return markers
  }

  const addMarkersListener = markers => {
    markers.map(marker => {
      marker.addListener('click', () => {
        const selectedHtlmMarker = getSelectedHtlmMarker(marker.caretaker)
        marker.editHtml(selectedHtlmMarker)
        setActiveCaretakers([marker.caretaker])
        handleBottomButton(false)
        saveFullMapDraft({ caretaker: marker.caretaker })
      })
    })
    setStateMarkers(markers)
  }

  const addClickMapListener = (markers, googleMapsMap) => {
    map.current.addEventListener(
      'click',
      () => {
        markers.map(marker => {
          const unselectedHtlmMarker = getUnselectedHtlmMarker(marker.caretaker)
          marker.editHtml(unselectedHtlmMarker)
        })
        setActiveCaretakers(undefined)
        handleBottomButton(true)
        saveFullMapDraft({ caretaker: undefined, cluster: undefined })
        saveDraftLocation(googleMapsMap, address)
      },
      true,
    )
  }

  const addDragendMapListener = googleMapsMap => {
    googleMapsMap.addListener('dragend', () => {
      handleRefreshButton(true)
      saveDraftLocation(googleMapsMap, address)
    })
  }

  const addMapListeners = (markers, googleMapsMap) => {
    addClickMapListener(markers, googleMapsMap)
    addDragendMapListener(googleMapsMap)
  }

  const addListeners = (markers, googleMapsMap) => {
    addMapListeners(markers, googleMapsMap)
    addMarkersListener(markers)
  }

  const addMarkerCluster = (markers, googleMapsMap) => {
    const cluster = new MarkerClusterer(googleMapsMap, markers, mcOptions)
    const clusterCaretakers = draft.get(fullMapDraft).cluster
    if (clusterCaretakers && clusterCaretakers.length > 0) {
      setActiveCaretakers(clusterCaretakers)
      handleBottomButton(false)
    }
    cluster.addListener('click', markerCluster => {
      if (googleMapsMap.getZoom() >= 20) {
        const caretakers = markerCluster.markers_.map(marker => marker.caretaker)
        setActiveCaretakers(caretakers)
        handleBottomButton(false)
        saveFullMapDraft({ cluster: caretakers })
      }
    })
    setStateCluster(cluster)
  }

  const getCaretakerFilter = () => {
    const draftFilter = draft.get(fullMapDraft)
    const urlFilter = getUrlParams().filter
    if (urlFilter) {
      return urlFilter.split(',')
    }

    if (draftFilter.filter && draftFilter.filter.length > 0) {
      return draftFilter.filter
    }

    return []
  }

  const searchCaretakers = async (searchOptions: {
    position: { latitude: number; longitude: number }
    caretakerFilter: string[]
  }) => {
    handleLoadingSearchHereButton(true)
    try {
      const caretakers = await getCaretakersLocation(
        searchOptions.position.latitude,
        searchOptions.position.longitude,
        caretakersLocationLimit,
        caretakersLocationOffset,
        searchOptions.caretakerFilter,
      )
      handleRefreshButton(false)
      handleLoadingSearchHereButton(false)
      return caretakers
    } catch (error) {
      const caretakerError = formatError(error)
      setErrors(caretakerError)
      handleLoadingSearchHereButton(false)
      return []
    }
  }

  const initialLoad = async () => {
    setFamilySearchingNumber()

    const defaultLocation = await getDefaultLocation()
    const defaultZoom = getDefaultZoom()
    const caretakerFilter = getCaretakerFilter()
    setFilters(caretakerFilter)
    const searchOptions = {
      position: { latitude: defaultLocation.lat, longitude: defaultLocation.lng },
      caretakerFilter,
    }

    Promise.all([searchCaretakers(searchOptions), loadGoogleMaps(defaultLocation, defaultZoom)]).then(
      ([offers, googleMaps]) => {
        if (googleMaps && offers.length > 0) {
          const markers = createMarkers(offers, googleMaps)
          addListeners(markers, googleMaps)
          addMarkerCluster(markers, googleMaps)
        }
      },
    )
  }

  React.useEffect(() => {
    initialLoad()
    setInnerHeight(window.innerHeight)
  }, [])

  React.useEffect(() => {
    saveFullMapDraft({ filters })
  }, [filters])

  const removeMarkers = () => {
    const markers = stateMarkers
    if (markers.length > 0) {
      markers.map(marker => {
        marker.onRemove()
      })
      const cluster = stateCluster
      cluster.clearMarkers()
    }

    setStateMarkers([])
    setStateCluster(undefined)
  }

  const updateMarkers = (caretakers, mapOfGoogleMaps) => {
    removeMarkers()

    if (caretakers.length > 0 && mapOfGoogleMaps) {
      const markers = createMarkers(caretakers, mapOfGoogleMaps)
      addListeners(markers, mapOfGoogleMaps)
      addMarkerCluster(markers, mapOfGoogleMaps)
    }
  }

  const updateAddress = updatedAddress => setAddress(updatedAddress)

  const updateLocation = async (place: google.maps.GeocoderResult) => {
    updateAddress(place.formatted_address)
    const searchOptions = {
      position: { latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() },
      caretakerFilter: filters,
    }
    const caretakers = await searchCaretakers(searchOptions)
    updateMarkers(caretakers, stateMap)
    setBounds(place)
    saveDraftLocation(stateMap, place.formatted_address)
  }

  const isActiveFilter = (newFilter: CaretakerLocationFilters) => filters.includes(newFilter)

  const updateFilter = (caretakerFilter: CaretakerLocationFilters) => {
    let updatedFilters

    if (filters.includes(caretakerFilter)) {
      updatedFilters = filters.filter(filter => filter !== caretakerFilter)
    } else {
      updatedFilters = [...filters]
      updatedFilters.push(caretakerFilter)
    }

    setFilters(updatedFilters)

    return updatedFilters
  }

  const onFilterButtonClick = async (caretakerFilter: CaretakerLocationFilters) => {
    const updatedFilters = updateFilter(caretakerFilter)
    saveFullMapDraft({ filter: updatedFilters })
    const searchOptions = {
      position: { latitude: stateMap.getCenter().lat(), longitude: stateMap.getCenter().lng() },
      caretakerFilter: updatedFilters,
    }
    const caretakers = await searchCaretakers(searchOptions)
    updateMarkers(caretakers, stateMap)
    saveDraftLocation(stateMap, address)
  }

  const onClickCaretaker = caretaker => {
    if (windowsWithNativeProps.ReactNativeWebView) {
      history.push(routeProvider.caretakerCard(caretaker.caretakerId))
    } else {
      openInNewTab(routeProvider.caretakerCard(caretaker.caretakerId))
    }
  }

  const onClickSearchHereButton = async () => {
    const searchOptions = {
      position: { latitude: stateMap.getCenter().lat(), longitude: stateMap.getCenter().lng() },
      caretakerFilter: filters,
    }
    const caretakers = await searchCaretakers(searchOptions)
    updateMarkers(caretakers, stateMap)
    saveDraftLocation(stateMap, address)
  }

  const redirectToFork = () => {
    const mapDraftValues = draft.get(fullMapDraft)
    draft.set(broadcastFormDraftName, {
      [broadcastFormSteps.serviceInformationStep.location]: {
        latitude: mapDraftValues.location.position.lat,
        longitude: mapDraftValues.location.position.lng,
        address: mapDraftValues.location.position.address,
      },
    })
    history.push(routeProvider.recommendationType)
  }

  const onClickModalButton = (place: google.maps.GeocoderResult) => {
    updateLocation(place)
    handleSearchInputModal(false)
  }

  return (
    <>
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      {isUserAuthenticated() ? <PrivateHeader secondLevel /> : <PublicHeader />}
      <FullMapWrapper>
        {showErrorNotification && <Error error={errorNotification} onClick={onClickErrorNotification} />}
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            {showSearchInputModal && <SearchInputModal onClickButton={onClickModalButton} />}
            <ContainerSearchBlock>
              <StyledSearchBlock>
                <div>
                  <Block marginBottom="tiny">
                    <Text menu>Viendo profesionales en...</Text>
                  </Block>
                  <Block>
                    <LocationSearchInput
                      address={address}
                      onPlaceChanged={updateLocation}
                      icon
                      placeholder="¿Dónde buscas profesionales?"
                    />
                  </Block>
                </div>
                <MediaQueryOwn maxWidth={theme.breakpoints.desktop - 1}>
                  <Block marginTop="base" flex>
                    <Icon content={eye} size="m" />
                    <Text>{familySearching} familias buscando en tu zona</Text>
                  </Block>
                </MediaQueryOwn>
                <Block marginTop="base" flex alignItems="baseline" justify={isDesktop() ? 'end' : null}>
                  <Text menu marginRight="mini">
                    Filtros
                  </Text>
                  <Block>
                    <TabButton
                      onClick={() => onFilterButtonClick(CaretakerLocationFilters.kids)}
                      active={isActiveFilter(CaretakerLocationFilters.kids)}
                    >
                      Niños
                    </TabButton>
                  </Block>
                  <Block marginLeft="base">
                    <TabButton
                      onClick={() => onFilterButtonClick(CaretakerLocationFilters.senior)}
                      active={isActiveFilter(CaretakerLocationFilters.senior)}
                    >
                      Mayores
                    </TabButton>
                  </Block>
                  <Block marginLeft="base">
                    <TabButton
                      onClick={() => onFilterButtonClick(CaretakerLocationFilters.extra)}
                      active={isActiveFilter(CaretakerLocationFilters.extra)}
                    >
                      Hogar
                    </TabButton>
                  </Block>
                </Block>
              </StyledSearchBlock>
            </ContainerSearchBlock>
            <MediaQueryOwn minWidth={theme.breakpoints.desktop}>
              <SearchFamilyWrapper>
                <ContainerSearchFamily flex>
                  <Icon content={eye} size="m" />
                  <Text>{familySearching} familias buscando en tu zona</Text>
                </ContainerSearchFamily>
              </SearchFamilyWrapper>
            </MediaQueryOwn>
          </>
        )}
        <FullMapCaretakersWrapper windowHeight={innerHeight}>
          {!isLoading && (
            <>
              {showRefreshButton && (
                <SearchHereButton onClick={onClickSearchHereButton} isLoading={isLoadingSearchHereButton} />
              )}
              {activeCaretakers && activeCaretakers.length > 0 && (
                <CaretakerCard caretakers={activeCaretakers} onClickCaretaker={onClickCaretaker} />
              )}
            </>
          )}
          <div ref={map} style={{ height: '100%' }} className="map-google"></div>
          {!isLoading && (
            <>
              {showBottomButton && (
                <StyledAskNearCaretakersButton mini primary onClick={redirectToFork}>
                  ¿Qué necesitas?
                </StyledAskNearCaretakersButton>
              )}
            </>
          )}
        </FullMapCaretakersWrapper>
      </FullMapWrapper>
    </>
  )
}



// WEBPACK FOOTER //
// ./src/views/FullMapCaretakers/FullMapCaretakers.tsx